import { Coins } from 'lucide-react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  formatDateTime,
  formatRand,
  getNum,
  getStr,
} from './_shared';

/**
 * OIL-1 — Round-Value Oil Purchase.
 *
 * "Oil amount is a round-rand value — common airtime denominators are
 * R10/20/50/100/200." When the oil "purchase" exactly matches an airtime
 * voucher denomination it's a strong signal the merchant is processing a
 * disguised airtime sale through the fuel-card terminal.
 *
 * Evidence keys (verified from live fraud.alert sample):
 *   rule_name, site_name, amount_rand, driver_name, escalations[],
 *   amount_cents, attendant_code, is_round_value, site_reference,
 *   driver_reference, transaction_datetime, vehicle_registration,
 *   transaction_reference, is_airtime_denomination
 */
const AIRTIME_DENOMINATIONS = [10, 20, 50, 100, 200];

export default function OilRoundAmountRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  const amountRand = getNum(evidence, 'amount_rand');
  const isAirtime = evidence?.is_airtime_denomination === true;
  const isRound = evidence?.is_round_value === true;
  const matchedDenom =
    typeof amountRand === 'number'
      ? AIRTIME_DENOMINATIONS.find((d) => d === amountRand)
      : undefined;
  const vehicleReg =
    getStr(evidence, 'vehicle_registration') ?? alert.asset_token ?? null;
  const siteName = getStr(evidence, 'site_name');
  const attendantCode =
    getStr(evidence, 'attendant_code') ?? alert.attendant_code ?? null;
  const txnTime = getStr(evidence, 'transaction_datetime');
  const escalations = Array.isArray(evidence?.escalations)
    ? (evidence!.escalations as unknown[]).filter(
        (e): e is string => typeof e === 'string',
      )
    : [];

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Coins className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <HeadlineLine>
          Oil purchase of {formatRand(amountRand)} is a round-rand amount
          {isAirtime || matchedDenom
            ? ' that matches a common airtime voucher denomination.'
            : ' — investigate whether this matches a disguised non-fuel sale.'}
        </HeadlineLine>
      </div>

      <div className="flex items-baseline gap-3 rounded-md border bg-muted/20 px-3 py-2">
        <span className="text-2xl font-bold tabular-nums">
          {formatRand(amountRand)}
        </span>
        {matchedDenom !== undefined && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            matches R{matchedDenom} airtime voucher
          </span>
        )}
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Round-rand amount">
          {isRound ? 'Yes' : 'No'}
        </EvidenceRow>
        <EvidenceRow label="Airtime denomination match">
          {isAirtime ? (
            <span className="font-medium text-red-700">Yes</span>
          ) : (
            'No'
          )}
        </EvidenceRow>
        <EvidenceRow label="Vehicle">
          <span className="font-mono text-xs">{vehicleReg ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Site">{siteName ?? '—'}</EvidenceRow>
        <EvidenceRow label="Attendant code">
          <span className="font-mono text-xs">{attendantCode ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Purchased at">{formatDateTime(txnTime)}</EvidenceRow>
        {escalations.length > 0 && (
          <EvidenceRow label="Escalations">
            <span className="text-xs">{escalations.join(', ')}</span>
          </EvidenceRow>
        )}
      </div>

      {amountRand === undefined && (
        <MissingContextLine message="Oil amount is missing — see raw evidence below." />
      )}
    </RendererShell>
  );
}
